import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api-client';
import { Button } from '../components/ui/Button';
import { ContextPanel, ExcelButton, PageHead } from '../components/ui/chrome';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../components/ui/ColumnSearch';
import { useNotice, type ProcessRun } from '../components/ui/Notice';

interface ScheduledJob {
  key: string;
  name: string;
  running?: boolean;
  startedAt?: string;
}

function statusLabel(status: ProcessRun['status']): string {
  if (status === 'running') return 'Running';
  if (status === 'done') return 'Finished';
  if (status === 'failed') return 'Failed';
  return 'Cancelled';
}

function clock(value: number | string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function ProcessesPage() {
  const notice = useNotice();
  const [selectedId, setSelectedId] = useState('');
  const [jobLines, setJobLines] = useState<string[]>([]);
  const [jobDone, setJobDone] = useState<boolean | null>(null);
  const [jobError, setJobError] = useState('');
  const logRef = useRef<HTMLDivElement>(null);
  const query = useQuery({
    queryKey: ['jobs'],
    queryFn: () => apiGet<{ jobs: ScheduledJob[] }>('/api/jobs'),
    refetchInterval: 2000,
  });
  const runningJobs = (query.data?.jobs || []).filter((job) => job.running);
  const scheduledKey = selectedId.startsWith('job:') ? selectedId.slice(4) : '';
  const selectedJob = runningJobs.find((job) => job.key === scheduledKey) || (query.data?.jobs || []).find((job) => job.key === scheduledKey);
  const selected = notice.processes.find((item) => item.id === selectedId);
  const sessionRunning = notice.processes.filter((item) => item.status === 'running');
  const sessionDone = notice.processes.filter((item) => item.status !== 'running');
  const columnSearch = useColumnSearch();
  const openLines = selected ? selected.lines : jobLines;
  const scheduledStatus: ProcessRun['status'] | '' = scheduledKey ? (jobDone === false ? 'failed' : jobDone === true ? 'done' : 'running') : '';

  useEffect(() => {
    if (selectedId && !selectedId.startsWith('job:') && !notice.processes.some((item) => item.id === selectedId)) setSelectedId('');
  }, [notice.processes, selectedId]);

  useEffect(() => {
    if (!scheduledKey) return;
    const controller = new AbortController();
    setJobLines([]);
    setJobDone(null);
    setJobError('');
    void (async () => {
      try {
        const response = await fetch(`/api/jobs/${scheduledKey}/logs`, { credentials: 'include', signal: controller.signal });
        if (!response.ok || !response.body) throw new Error(`Logs failed (${response.status})`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const chunk = await reader.read();
          if (chunk.done) break;
          buffer += decoder.decode(chunk.value, { stream: true });
          const parts = buffer.split('\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            const line = part.trim();
            if (!line) continue;
            const event = JSON.parse(line) as { type?: string; message?: string; ok?: boolean };
            if (event.type === 'line' && event.message) setJobLines((current) => [...current, event.message as string]);
            if (event.type === 'done') setJobDone(event.ok !== false);
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) setJobError(err instanceof Error ? err.message : 'Logs failed');
      }
    })();
    return () => controller.abort();
  }, [scheduledKey]);

  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [selectedId, openLines.length]);

  const processRows: {
    key: string;
    title: string;
    kind: string;
    status: string;
    running: boolean;
    failed: boolean;
    started: string;
    onOpen: () => void;
    onCancel?: () => void;
    onDismiss?: () => void;
  }[] = [
    ...sessionRunning.map((item) => ({
      key: item.id, title: item.title, kind: 'Session', status: statusLabel(item.status), running: item.status === 'running', failed: item.status === 'failed', started: clock(item.startedAt),
      onOpen: () => setSelectedId(item.id), onCancel: () => notice.cancelProcess(item.id),
    })),
    ...runningJobs.map((job) => ({
      key: job.key, title: job.name, kind: 'Scheduled job', status: 'Running', running: true, failed: false, started: clock(job.startedAt),
      onOpen: () => setSelectedId(`job:${job.key}`),
    })),
    ...sessionDone.map((item) => ({
      key: item.id, title: item.title, kind: 'Session', status: statusLabel(item.status), running: false, failed: item.status === 'failed', started: clock(item.startedAt),
      onOpen: () => setSelectedId(item.id), onDismiss: () => notice.dismissProcess(item.id),
    })),
  ].filter((row) => columnSearch.matches({ process: row.title, kind: row.kind, status: row.status, started: row.started }));

  const showPanel = Boolean(selected || scheduledKey);
  const panelTitle = selected?.title || selectedJob?.name || 'Scheduled job';
  const panelStatus = selected ? statusLabel(selected.status) : scheduledStatus ? statusLabel(scheduledStatus) : 'Scheduled job';
  const panelStarted = selected ? clock(selected.startedAt) : clock(selectedJob?.startedAt);

  return (
    <div>
      <PageHead crumbs={[{ label: 'Operate' }, { label: 'Processes' }]} title="Processes" subtitle="Session runs, and scheduled jobs that are running now" />
      <div className={showPanel ? 'grid items-stretch gap-4 max-md:block xl:grid-cols-[3fr_2fr]' : ''}>
        <div className={`min-w-0 flex-1 ${showPanel ? 'max-md:hidden' : ''}`}>
          <div className="mb-3 flex justify-end">
            <ExcelButton filename="Processes" headers={['Process', 'Kind', 'Status', 'Started']} rows={processRows.map((row) => [row.title, row.kind, row.status, row.started])} />
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="bo-cards min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-3 py-2 font-medium">Process</th>
                  <th className="py-2 font-medium">Kind</th>
                  <th className="py-2 font-medium">Status</th>
                  <th className="py-2 font-medium">Started</th>
                  <th className="py-2 pr-3 text-right font-medium"><span className="mr-2">Actions</span><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th>
                </tr>
                {columnSearch.open ? <ColumnFilterInputs columns={[{ key: 'process', label: 'Process' }, { key: 'kind', label: 'Kind' }, { key: 'status', label: 'Status' }, { key: 'started', label: 'Started' }]} draft={columnSearch.draft} onChange={columnSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {processRows.length === 0 ? (
                  <tr><td className="px-3 py-6 text-gray-500" colSpan={5}>{sessionRunning.length || runningJobs.length || sessionDone.length ? 'No processes match.' : 'No processes in this session, and no scheduled job is running.'}</td></tr>
                ) : null}
                {processRows.map((row) => (
                  <ProcessRow key={row.key} title={row.title} kind={row.kind} status={row.status} running={row.running} failed={row.failed} started={row.started} onOpen={row.onOpen} onCancel={row.onCancel} onDismiss={row.onDismiss} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {showPanel ? (
          <ContextPanel label={scheduledKey ? 'Scheduled job' : 'Process'} title={panelTitle} subtitle={panelStatus} status={panelStarted} onClose={() => setSelectedId('')}>
            {(selected?.status === 'running' || scheduledStatus === 'running') ? (
              <div className="mb-3 flex items-center gap-3">
                <span className="bo-spinner" aria-hidden="true" />
                <span className="text-sm font-medium">Running</span>
              </div>
            ) : null}
            <div ref={logRef} className="max-h-[28rem] overflow-auto">
              <div className="bo-terminal">
                {(openLines.length ? openLines : ['']).map((line, index) => <div key={index}>{line}</div>)}
              </div>
            </div>
            {selected?.error || jobError ? <p className="mt-2 text-sm text-red-600">{selected?.error || jobError}</p> : null}
            <div className="mt-4 flex justify-end">
              {selected?.status === 'running' ? <Button variant="outline" onClick={() => notice.cancelProcess(selected.id)}>Cancel</Button> : null}
            </div>
          </ContextPanel>
        ) : null}
      </div>
    </div>
  );
}

function ProcessRow({ title, kind, status, running, failed, started, onOpen, onCancel, onDismiss }: {
  title: string;
  kind: string;
  status: string;
  running?: boolean;
  failed?: boolean;
  started: string;
  onOpen: () => void;
  onCancel?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <tr className="border-b border-gray-100 dark:border-gray-800">
      <td className="px-3 py-3" data-label="Process">{title}</td>
      <td className="py-3" data-label="Kind">{kind}</td>
      <td className={`py-3 ${running ? 'text-blue-600' : failed ? 'text-red-600' : 'text-gray-600 dark:text-gray-300'}`} data-label="Status">{status}</td>
      <td className="py-3" data-label="Started">{started}</td>
      <td className="py-3 pr-3 text-right" data-label="">
        <div className="flex justify-end gap-2">
          <Button size="xs" variant="outline" onClick={onOpen}>Open</Button>
          {onCancel ? <Button size="xs" variant="outline" onClick={onCancel}>Cancel</Button> : null}
          {onDismiss ? <Button size="xs" variant="outline" onClick={onDismiss}>Dismiss</Button> : null}
        </div>
      </td>
    </tr>
  );
}
